import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { interval, Subject, takeUntil } from 'rxjs';
import { Account, tiers } from '../../../service/account.service';
import { AdminService } from '../../../service/admin.service';
import { AvatarComponent } from '../../../helper/avatar/avatar.component';

interface VRChatCheckResult {
  connected: boolean;
  up_to_date: boolean;
  stored_username?: string;
  current_display_name?: string;
  vrchat_id?: string;
  message?: string;
  error?: boolean;
}

@Component({
  selector: 'app-admin-support',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatSnackBarModule,
    MatTooltipModule,
    AvatarComponent
  ],
  templateUrl: './support.component.html',
  styleUrl: './support.component.less'
})
export class AdminSupportComponent implements OnInit, OnDestroy {
  adminService = inject(AdminService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  snackBar = inject(MatSnackBar);

  user: Account | null = null;
  isLoading = true;
  vrchatCheckResult: VRChatCheckResult | null = null;
  checkingVRChat = false;
  updatingVRChat = false;
  lastUpdated: Date | null = null;

  private destroy$ = new Subject<void>();
  private pollInterval = interval(5000); // Poll every 5 seconds for live updates
  private pollSubscription: any;

  ngOnInit() {
    const userID = this.route.snapshot.paramMap.get('userID');
    if (!userID) {
      this.router.navigate(['/admin/users']);
      return;
    }

    this.loadUser();
    this.checkVRChatUsername();

    // Set up polling for live updates
    this.pollSubscription = this.pollInterval
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadUser(true); // Silent refresh
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
  }

  loadUser(silent: boolean = false) {
    if (!silent) {
      this.isLoading = true;
    }

    const userID = this.route.snapshot.paramMap.get('userID');
    if (!userID) return;

    this.adminService.getUser(userID).subscribe({
      next: (user) => {
        this.user = user;
        this.isLoading = false;
        this.lastUpdated = new Date();
        
      },
      error: (error) => {
        console.error('Error loading user:', error);
        this.isLoading = false;
        if (!silent) {
          this.snackBar.open('Failed to load user information', 'Close', {
            duration: 5000
          });
        }
      }
    });
  }

  checkVRChatUsername() {
    const userID = this.route.snapshot.paramMap.get('userID');
    if (!userID) return;

    this.checkingVRChat = true;
    this.adminService.checkVRChatUsername(userID).subscribe({
      next: (result) => {
        this.vrchatCheckResult = result;
        this.checkingVRChat = false;

        if (result.error) {
          this.snackBar.open('Error checking VRChat username', 'Close', {
            duration: 5000
          });
        }
      },
      error: (error) => {
        console.error('Error checking VRChat username:', error);
        this.checkingVRChat = false;
        this.snackBar.open('Failed to check VRChat username', 'Close', {
          duration: 5000
        });
      }
    });
  }

  refresh() {
    this.loadUser();
    this.checkVRChatUsername();
  }

  updateVRChatUsername() {
    const userID = this.route.snapshot.paramMap.get('userID');
    if (!userID) return;

    this.updatingVRChat = true;
    this.adminService.updateVRChatUsername(userID).subscribe({
      next: (result) => {
        this.updatingVRChat = false;
        if (result.success) {
          this.snackBar.open(result.message || 'VRChat username updated', 'Close', {
            duration: 5000
          });
          // Reload user to get updated data
          this.loadUser();
          // Re-check to update the check result
          this.checkVRChatUsername();
        } else {
          this.snackBar.open(result.message || 'Failed to update VRChat username', 'Close', {
            duration: 5000
          });
        }
      },
      error: (error) => {
        console.error('Error updating VRChat username:', error);
        this.updatingVRChat = false;
        this.snackBar.open('Failed to update VRChat username', 'Close', {
          duration: 5000
        });
      }
    });
  }

  getUserSubscriptionTier(): any {
    console.log("getUserSubscriptionTier", this.user, this.user?.connections.patreon);
    if (!this.user || !this.user.connections.patreon) {
      return tiers[-1];
    }
    return this.user.connections.patreon.subscriptionTier || tiers[-1];
  }
}

